import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    // Initialize token from localStorage on mount
    try {
      return localStorage.getItem("scaffoldx_token") || null;
    } catch (e) {
      console.error("Error reading token from localStorage:", e);
      return null;
    }
  });

  // Configure axios defaults whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Global 401 interceptor — clear expired token + redirect gracefully.
  // NOTE: The Doserne Academy sub-app (/doserne/*) has its own auth (DOSERNE_TOKEN)
  // and its own 401 handling. Any 401 from a Doserne endpoint OR while the user is
  // browsing /doserne/* must NOT kick them out to the main /login page.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        // Auth-endpoint 401s are expected during login and should not force a redirect.
        const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/me");
        const isDoserneCall = url.includes("/api/doserne/");
        const isOnDoserne = path.startsWith("/doserne");
        if (status === 401 && !isAuthCall && !isDoserneCall && !isOnDoserne) {
          try {
            localStorage.removeItem("scaffoldx_token");
            localStorage.removeItem("scaffoldx_user");
          } catch (_e) { /* ignore */ }
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== "/login") {
            window.location.href = "/login?expired=1";
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Get token from state (already loaded from localStorage)
      const storedToken = token;
      
      if (!storedToken) {
        console.log("No token found, skipping auth check");
        setLoading(false);
        return;
      }
      
      try {
        console.log("Checking auth with token...");
        // Explicitly include the token in the request header
        const response = await axios.get(`${API}/auth/me`, {
          headers: {
            Authorization: `Bearer ${storedToken}`
          }
        });
        console.log("Auth check successful:", response.data?.email);
        setUser(response.data);
      } catch (error) {
        // Token is invalid/expired - clear it
        console.error("Auth check failed:", error?.response?.status, error?.response?.data?.detail || error.message);
        setUser(null);
        setToken(null);
        try {
          localStorage.removeItem("scaffoldx_token");
        } catch (e) {
          console.error("Error clearing token from localStorage:", e);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []); // Only run once on mount

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, ...userData } = response.data;
    
    // Clear any previous impersonation state when doing a fresh login
    try {
      localStorage.removeItem("impersonation_info");
      localStorage.removeItem("admin_view_as_role");
      // Also drop any Doserne session so the two identities don't overlap
      // in the same browser (a Doserne member using their own account here
      // shouldn't keep their old Doserne-side session active).
      localStorage.removeItem("DOSERNE_TOKEN");
    } catch (e) {
      console.error("Error clearing impersonation state:", e);
    }
    
    // Save to state and localStorage
    setToken(newToken);
    setUser(userData);
    try {
      localStorage.setItem("scaffoldx_token", newToken);
      console.log("Token saved to localStorage after login");
    } catch (e) {
      console.error("Error saving token to localStorage:", e);
    }
    
    return userData;
  };

  const requestLoginCode = async (loginEmail) => {
    await axios.post(`${API}/auth/request-code`, { email: loginEmail });
  };

  const loginWithCode = async (loginEmail, loginCode) => {
    const response = await axios.post(`${API}/auth/verify-code`, { email: loginEmail, code: loginCode });
    const { token: newToken, ...userData } = response.data;
    try {
      localStorage.removeItem("impersonation_info");
      localStorage.removeItem("admin_view_as_role");
      localStorage.removeItem("DOSERNE_TOKEN");
    } catch (e) { /* ignore */ }
    setToken(newToken);
    setUser(userData);
    try {
      localStorage.setItem("scaffoldx_token", newToken);
    } catch (e) { /* ignore */ }
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, ...user } = response.data;
    
    setToken(newToken);
    setUser(user);
    try {
      localStorage.setItem("scaffoldx_token", newToken);
      console.log("Token saved to localStorage after register");
    } catch (e) {
      console.error("Error saving token to localStorage:", e);
    }
    
    // Return user with token so it can be used immediately after registration
    return { ...user, token: newToken };
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/client";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    
    // Clear any previous impersonation state when doing OAuth login
    try {
      localStorage.removeItem("impersonation_info");
      localStorage.removeItem("admin_view_as_role");
    } catch (e) {
      console.error("Error clearing impersonation state:", e);
    }
    
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Continue with logout even if server call fails
    }
    
    setUser(null);
    setToken(null);
    try {
      localStorage.removeItem("scaffoldx_token");
      // Clear impersonation and view-as state on logout
      localStorage.removeItem("impersonation_info");
      localStorage.removeItem("admin_view_as_role");
      localStorage.removeItem("admin_token_backup");
    } catch (e) {
      console.error("Error clearing token from localStorage:", e);
    }
  };

  const value = {
    user,
    loading,
    token,
    login,
    register,
    requestLoginCode,
    loginWithCode,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
